















































import Vue from "@/shared/node_modules/vue";
import Component from "@/shared/node_modules/vue-class-component";
import { Prop } from "@/shared/node_modules/vue-property-decorator";
import { audioToWav } from "./audioToWav";

@Component
export default class VoiceRecorder extends Vue {
  @Prop({ default: null }) value!: any;
  recorder: any = null;
  stream: MediaStream | null = null;
  chunks: Blob[] = [];
  audioUrl: string = "";
  recording: boolean = false;
  processing: boolean = false;
  elapsed: number = 0;
  timer: number | null = null;
  error: string = "";
  privacyAccepted: boolean = false;

  get elapsedLabel() {
    const minutes = Math.floor(this.elapsed / 60);
    const seconds = (this.elapsed % 60).toString().padStart(2, "0");
    return `${minutes}:${seconds}`;
  }

  mounted() {
    if (this.value && this.value.blob) this.setPreview(this.value.blob);
    if (this.value && this.value.privacyAccepted) this.privacyAccepted = true;
  }

  beforeDestroy() {
    this.release();
    if (this.audioUrl) URL.revokeObjectURL(this.audioUrl);
  }

  preferredMime(): string | undefined {
    const values = ["audio/mp4", "audio/webm;codecs=opus", "audio/webm"];
    const Recorder = (window as any).MediaRecorder;
    return values.find((x) => Recorder && Recorder.isTypeSupported(x));
  }

  async start() {
    this.error = "";
    if (!this.privacyAccepted) {
      this.error = this.$t(
        "You must accept the notice before recording",
      ).toString();
      return;
    }
    this.$emit("input", null);
    if (this.audioUrl) {
      URL.revokeObjectURL(this.audioUrl);
      this.audioUrl = "";
    }
    const Recorder = (window as any).MediaRecorder;
    if (!navigator.mediaDevices || !Recorder) {
      this.error = this.$t(
        "Audio recording is not supported by this browser",
      ).toString();
      return;
    }
    try {
      this.stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = this.preferredMime();
      this.recorder = mime
        ? new Recorder(this.stream, { mimeType: mime })
        : new Recorder(this.stream);
      this.chunks = [];
      this.elapsed = 0;
      this.recorder.ondataavailable = (event: any) => {
        if (event.data && event.data.size > 0) this.chunks.push(event.data);
      };
      this.recorder.onstop = () => void this.complete();
      this.recorder.start(1000);
      this.recording = true;
      this.timer = window.setInterval(() => {
        this.elapsed += 1;
        if (this.elapsed >= 120) this.stop();
      }, 1000);
    } catch (_) {
      this.error = this.$t(
        "Microphone permission is required to record an answer",
      ).toString();
      this.release();
    }
  }

  stop() {
    if (this.recorder && this.recorder.state !== "inactive")
      this.recorder.stop();
    this.recording = false;
    if (this.timer !== null) window.clearInterval(this.timer);
    this.timer = null;
  }

  async complete() {
    const mime =
      (this.recorder && this.recorder.mimeType) ||
      this.preferredMime() ||
      "audio/webm";
    const recorded = new Blob(this.chunks, { type: mime });
    this.release();
    if (recorded.size === 0) {
      this.error = this.$t("The recording is empty or too large").toString();
      this.$emit("input", null);
      return;
    }
    this.processing = true;
    let blob: Blob;
    try {
      blob = await audioToWav(recorded);
    } catch (_) {
      this.error = this.$t("The recording could not be processed").toString();
      this.$emit("input", null);
      this.processing = false;
      return;
    }
    this.processing = false;
    if (blob.size > 5 * 1024 * 1024) {
      this.error = this.$t("The recording is empty or too large").toString();
      this.$emit("input", null);
      return;
    }
    this.setPreview(blob);
    const reader = new FileReader();
    reader.onload = () => {
      this.$emit("input", {
        blob,
        audioBase64: reader.result,
        audioMimeType: "audio/wav",
        privacyAccepted: this.privacyAccepted,
      });
    };
    reader.readAsDataURL(blob);
  }

  setPreview(blob: Blob) {
    if (this.audioUrl) URL.revokeObjectURL(this.audioUrl);
    this.audioUrl = URL.createObjectURL(blob);
  }

  release() {
    if (this.stream) this.stream.getTracks().forEach((track) => track.stop());
    this.stream = null;
    this.recorder = null;
  }
}
